import {
  Col,
  Container,
  Card,
  CardBody,
  Row,
  FormGroup,
  Label,
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import { Epin, TransferPinReport } from "../../utils/Constant";
import Breadcrumbs from "../../CommonElements/Breadcrumbs/Breadcrumbs";
import DatePicker from "react-datepicker";
import { useEffect, useState, useMemo } from "react";
import { decryptData } from "../../utils/helper/Crypto";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { IoMdPrint } from "react-icons/io";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import DataTable, { TableColumn } from "react-data-table-component";
import { useCommonService } from "../../Service/CommonService/Commonservice";
import { useSweetAlert } from "../../Context/SweetAlertContext";
import Datanotfound from "../PageNotFound/Datanotfound";
import CustomPagination from "../CustomPagination";
import { IoSearchSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import "./Searchpin.scss";

interface TableRow {
  [key: string]: any;
}

const TransferEPinReport = () => {
  const { showAlert } = useSweetAlert();
  const { ApiCalling } = useCommonService();
  const navigator = useNavigate();

  // State to hold the FULL dataset from the API
  const [fullData, setFullData] = useState<TableRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [toDate, setToDate] = useState(new Date());

  const [SearchValue, setSearchValue] = useState("Sent");
  const [filterText, setFilterText] = useState("");
  const [columns, setColumns] = useState<TableColumn<TableRow>[]>([]);
  const [loading, setLoading] = useState(false);

  const userId = decryptData(localStorage.getItem("clientId") as string);

  // Navigation helper useEffect
  useEffect(() => {
    (window as any).handleAction = (type: string) => {
      navigator(`${import.meta.env.BASE_URL}/${type}`);
    };
  }, [navigator]);

  // Data Fetch function
  const FetchData = async () => {
    if (fromDate > toDate) {
      showAlert("Error", "From Date cannot be greater than To Date");
      return;
    }

    setLoading(true);

    const formattedFromDate = format(fromDate, "dd-MMMM-yyyy");
    const formattedToDate = format(toDate, "dd-MMMM-yyyy");

    const obj = {
      FromDate: formattedFromDate,
      ToDate: formattedToDate,
      UserId: userId,
      searchTerm: filterText, // Correct parameter name
      SearchValue: SearchValue,
      ActionMode: "Select",
    };

    try {
      const res = await ApiCalling({
        procName: "EPinTransferReport",
        Para: JSON.stringify(obj),
      });

      if (Array.isArray(res) && res.length > 0) {
        // We're cleaning the data and setting the full dataset
        const cleanedData = res.map(({ TotalCount, ...rest }) => rest);
        setFullData(cleanedData);

        // Set columns from the first object in the cleaned data
        const keys = Object.keys(cleanedData[0]);
        const list: TableColumn<TableRow>[] = keys.map((col) => ({
          name: col,
          selector: (row) => row[col],
          sortable: true,
          wrap: true,
          cell: (row) => (
            <div dangerouslySetInnerHTML={{ __html: row[col] }} />
          ),
        }));
        setColumns(list);
      } else {
        setFullData([]);
        setColumns([]);
      }
    } catch (error) {
      console.error("Data fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Trigger data fetch whenever the search criteria change
  useEffect(() => {
    FetchData();
  }, [SearchValue, fromDate, toDate, filterText, userId]);

  // Pagination logic: Slice the full data to get the data for the current page
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    return fullData.slice(start, end);
  }, [fullData, currentPage, perPage]);

  // Update total rows whenever the full data changes
  useEffect(() => {
    setTotalRows(fullData.length);
  }, [fullData]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to the first page on new search
    // FetchData will be triggered by the useEffect since filterText is in its dependency array
  };

  // ✅ Clean Export Data and export functions
  const cleanDataForExport = (data: TableRow[]) => {
    return data.map((row) => {
      const cleanedRow: any = {};
      for (let key in row) {
        if (typeof row[key] === "string") {
          const div = document.createElement("div");
          div.innerHTML = row[key];
          const inputElement = div.querySelector("input");
          if (inputElement) {
            cleanedRow[key] = inputElement.value || "";
          } else {
            cleanedRow[key] = div.textContent || div.innerText || "";
          }
        } else {
          cleanedRow[key] = row[key];
        }
      }
      return cleanedRow;
    });
  };

  const exportToExcel = (data: TableRow[]) => {
    if (data.length === 0) {
      showAlert("Oops!", "No data available at the moment.");
      return;
    }
    const cleanedData = cleanDataForExport(data);
    const worksheet = XLSX.utils.json_to_sheet(cleanedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "TableData.xlsx");
  };

  const exportAndPrintCSV = (data: TableRow[]) => {
    if (data.length === 0) {
      showAlert("Oops!", "No data available at the moment.");
      return;
    }
    const cleanedData = cleanDataForExport(data);
    const csvData = XLSX.utils.sheet_to_csv(
      XLSX.utils.json_to_sheet(cleanedData)
    );
    printCSVData(csvData);
  };

  const printCSVData = (csvData: string) => {
    const escapeHtml = (text: string) =>
      text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const rows = csvData
      .split("\n")
      .map((row) => row.split(","))
      .filter((row) => row.length > 1 && row.some((cell) => cell.trim() !== ""));

    if (rows.length === 0) {
      showAlert("Oops!", "No valid data to print.");
      return;
    }

    const printWindow = window.open("", "", "height=900,width=1200");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(`
        <html>
        <head>
          <title>Printable Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            h2 { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #f2f2f2; }
            @media print { body { margin: 10mm; } button { display: none; } }
          </style>
        </head>
        <body>
          <h2>Report</h2>
          <table>
            <thead>
              <tr>${rows[0].map((cell) => `<th>${escapeHtml(cell.trim())}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${rows.slice(1).map((row) =>
                `<tr>${row.map((cell) => `<td>${escapeHtml(cell.trim())}</td>`).join("")}</tr>`
              ).join("")}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <div className="page-body">
      <Breadcrumbs
        mainTitle={TransferPinReport}
        parent={Epin}
        ChildName={TransferPinReport}
      />
      <Container fluid className="innerPageCard">
        <form onSubmit={handleSubmit}>
          <Row>
            <Col xl="12">
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label>From Date:</Label>
                    <DatePicker
                      className="form-control"
                      selected={fromDate}
                      onChange={(date: Date | null) => date && setFromDate(date)}
                      dateFormat="dd-MMMM-yyyy"
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>To Date:</Label>
                    <DatePicker
                      className="form-control"
                      selected={toDate}
                      onChange={(date: Date | null) => date && setToDate(date)}
                      dateFormat="dd-MMMM-yyyy"
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Status</Label>
                    <select
                      onChange={(e) => setSearchValue(e.target.value)}
                      value={SearchValue}
                      name="SearchValue"
                      className="form-control btn-square form-select"
                    >
                      <option value="Sent">Sent</option>
                      <option value="Received">Received</option>
                    </select>
                  </FormGroup>
                </Col>
                <Col md="3" className="d-flex align-items-center">
                  <button type="submit" className="btn btn-primary mt-0 mt-md-3">
                    <IoSearchSharp /> Search
                  </button>
                </Col>
              </Row>
            </Col>
          </Row>
        </form>

        <Col className="mb-3 mt-3 mt-md-0">
          <Card>
            <CardBody>
              <DataTable
                data={paginatedData}
                columns={columns}
                pagination={false}
                highlightOnHover
                subHeader
                subHeaderComponent={
                  <>
                    {loading && (
                      <Col className="d-flex justify-content-center">
                        <div className="spinner-border text-primary" role="status" />
                      </Col>
                    )}
                    {(fullData.length > 0 || filterText) && (
                      <Row className="p-0 m-0 align-items-center justify-content-between">
                        <Col md={12} className="d-flex align-items-center position-relative">
                          <IoSearchSharp
                            className="position-absolute"
                            style={{
                              top: "50%",
                              left: "30px",
                              fontSize: "20px",
                              transform: "translateY(-50%)",
                              color: "#999",
                            }}
                          />
                          <input
                            className="form-control w-100 ps-5"
                            placeholder="Search value..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                          />
                        </Col>
                        {/* <Col md="auto" className="d-flex align-items-center">
                          <Btn
                            color="primary px-2 py-2"
                            onClick={() => exportAndPrintCSV(fullData)}
                            style={{ marginRight: "10px" }}
                          >
                            <IoMdPrint size={20} /> Print
                          </Btn>
                          <Btn
                            color="primary px-2 py-2"
                            onClick={() => exportToExcel(fullData)}
                          >
                            <PiMicrosoftExcelLogoFill size={20} /> Excel
                          </Btn>
                        </Col> */}
                      </Row>
                    )}
                  </>
                }
                noDataComponent={<Datanotfound />}
              />
              {(fullData.length > 0 || filterText) && (
                <CustomPagination
                  currentPage={currentPage}
                  perPage={perPage}
                  totalRows={totalRows}
                  onPageChange={setCurrentPage}
                  onRowsPerPageChange={(newPerPage) => {
                    setPerPage(newPerPage);
                    setCurrentPage(1);
                  }}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Container>
    </div>
  );
};

export default TransferEPinReport;